import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Custom OnboardingTour component
 * A lightweight tour implementation compatible with React 19
 */
const OnboardingTour = ({
  steps,
  run,
  stepIndex = 0,
  onCallback,
  continuous = true,
  showProgress = true,
  showSkipButton = true
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(stepIndex);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);
  const [visibleSteps, setVisibleSteps] = useState([]);
  const retryCountRef = useRef(0);

  // Update current step when stepIndex prop changes
  useEffect(() => {
    setCurrentStep(stepIndex);
  }, [stepIndex]);

  // Filter steps to only include those with visible targets
  // Use useEffect with retry mechanism to wait for DOM elements
  useEffect(() => {
    if (!run || !steps || steps.length === 0) {
      setVisibleSteps([]);
      retryCountRef.current = 0;
      return;
    }

    const findVisibleSteps = () => {
      const visible = steps.filter(step => {
        const target = document.querySelector(step.target);
        return target !== null;
      });

      if (visible.length > 0) {
        setVisibleSteps(visible);
        retryCountRef.current = 0;
        return true;
      }
      return false;
    };

    // Try immediately first
    if (findVisibleSteps()) {
      return;
    }

    // If no elements found, retry with increasing delays
    const maxRetries = 10;
    const retryDelays = [100, 200, 300, 500, 750, 1000, 1500, 2000, 2500, 3000];

    const scheduleRetry = () => {
      if (retryCountRef.current >= maxRetries) {
        return;
      }

      const delay = retryDelays[retryCountRef.current] || 1000;
      retryCountRef.current++;

      setTimeout(() => {
        if (!findVisibleSteps() && run) {
          scheduleRetry();
        }
      }, delay);
    };

    scheduleRetry();

    return () => {
      retryCountRef.current = maxRetries; // Stop retries on cleanup
    };
  }, [steps, run]);

  // Find and highlight the target element
  useEffect(() => {
    if (!run || !visibleSteps || visibleSteps.length === 0) {
      setTargetRect(null);
      return;
    }

    const step = visibleSteps[currentStep];
    if (!step) {
      // No more steps, end tour
      onCallback?.({ action: 'next', index: currentStep, status: 'finished', type: 'tour:end' });
      return;
    }

    const updatePosition = () => {
      const target = document.querySelector(step.target);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);

        // Calculate tooltip position (using viewport coordinates for fixed positioning)
        const padding = 12;
        const tooltipWidth = 360;
        const tooltipHeight = 180;

        let top, left;
        const placement = step.placement || 'bottom';

        switch (placement) {
          case 'top':
            top = rect.top - tooltipHeight - padding;
            left = rect.left + rect.width / 2 - tooltipWidth / 2;
            break;
          case 'bottom':
            top = rect.bottom + padding;
            left = rect.left + rect.width / 2 - tooltipWidth / 2;
            break;
          case 'left':
            top = rect.top + rect.height / 2 - tooltipHeight / 2;
            left = rect.left - tooltipWidth - padding;
            break;
          case 'right':
            top = rect.top + rect.height / 2 - tooltipHeight / 2;
            left = rect.right + padding;
            break;
          default:
            top = rect.bottom + padding;
            left = rect.left + rect.width / 2 - tooltipWidth / 2;
        }

        // Keep tooltip within viewport
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        if (left < 10) left = 10;
        if (left + tooltipWidth > viewportWidth - 10) left = viewportWidth - tooltipWidth - 10;
        if (top < 10) top = 10;
        if (top + tooltipHeight > viewportHeight - 10) {
          // Place above if doesn't fit below
          top = rect.top - tooltipHeight - padding;
          if (top < 10) top = 10;
        }

        setTooltipPosition({ top, left });
      }
    };

    const scrollAndPosition = () => {
      const target = document.querySelector(step.target);
      if (target) {
        // Scroll element into view first
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Wait for scroll to complete, then update position
        setTimeout(updatePosition, 400);
      }
    };

    // Initial positioning after a small delay
    const timer = setTimeout(scrollAndPosition, 100);

    // Also update position on scroll/resize
    const handleScrollResize = () => updatePosition();
    window.addEventListener('scroll', handleScrollResize, { passive: true });
    window.addEventListener('resize', handleScrollResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScrollResize);
      window.removeEventListener('resize', handleScrollResize);
    };
  }, [run, visibleSteps, currentStep, onCallback]);

  const handleNext = useCallback(() => {
    if (currentStep < visibleSteps.length - 1) {
      setCurrentStep(currentStep + 1);
      onCallback?.({ action: 'next', index: currentStep, status: 'running', type: 'step:after' });
    } else {
      // Tour finished
      onCallback?.({ action: 'next', index: currentStep, status: 'finished', type: 'tour:end' });
    }
  }, [currentStep, visibleSteps, onCallback]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      onCallback?.({ action: 'prev', index: currentStep, status: 'running', type: 'step:before' });
    }
  }, [currentStep, onCallback]);

  const handleSkip = useCallback(() => {
    onCallback?.({ action: 'skip', index: currentStep, status: 'skipped', type: 'tour:end' });
  }, [currentStep, onCallback]);

  const handleClose = useCallback(() => {
    onCallback?.({ action: 'close', index: currentStep, status: 'skipped', type: 'tour:end' });
  }, [currentStep, onCallback]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!run) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handleBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [run, handleSkip, handleNext, handleBack]);

  if (!run || !visibleSteps || visibleSteps.length === 0 || !targetRect) {
    return null;
  }

  const step = visibleSteps[currentStep];
  if (!step) return null;

  const isLast = currentStep === visibleSteps.length - 1;
  const isFirst = currentStep === 0;

  const content = t(step.content);

  const tourContent = (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[9998] pointer-events-auto"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onClick={handleClose}
      />

      {/* Spotlight */}
      <div
        className="fixed z-[9999] pointer-events-none rounded-xl"
        style={{
          top: targetRect.top - 8,
          left: targetRect.left - 8,
          width: targetRect.width + 16,
          height: targetRect.height + 16,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
          transition: 'all 0.3s ease-in-out'
        }}
      />

      {/* Tooltip */}
      <Card
        ref={tooltipRef}
        className="fixed z-[10000] w-[360px] shadow-xl border-2 border-primary/20"
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          maxWidth: 'calc(100vw - 20px)'
        }}
      >
        <CardContent className="p-4">
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-1 right-1 h-10 w-10"
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </Button>

          {/* Content */}
          <div className="pr-6 mb-4">
            <p className="text-sm leading-relaxed text-gray-700">{content}</p>
          </div>

          {/* Progress */}
          {showProgress && (
            <div className="flex items-center gap-1 mb-3">
              {visibleSteps.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1.5 rounded-full transition-all ${
                    idx <= currentStep ? 'bg-primary w-4' : 'bg-gray-200 w-2'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <div>
              {showSkipButton && !isLast && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSkip}
                  className="text-gray-500 text-sm"
                >
                  {t('onboarding.tour.skip')}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBack}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {t('onboarding.tour.back')}
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNext}
                className="bg-primary hover:bg-primary/90"
              >
                {isLast ? t('onboarding.tour.finish') : t('onboarding.tour.next')}
                {!isLast && <ChevronRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );

  return createPortal(tourContent, document.body);
};

export default OnboardingTour;
